import { useEffect, useMemo, useRef, useState } from 'react';

import { reportLifecycleDiagnostic } from '../../app/api/lifecycleDiagnostics';
import { guided } from '../../app/guidance/fieldHelpRegistryCore';
import { deliveryFieldHelp } from '../../app/guidance/fieldHelpRegistry';
import { Card, Page } from '../../app/layout/Page';
import { exportApi } from './api';
import type { ExportApi } from './api';
import type {
  BundleImportPreview,
  ConflictResolution,
  ExportFormat,
  ExportPreview,
  ExportRunRecord,
  ExportSelection,
  ImportedProjectSnapshot,
  RouteColorMode,
} from './types';

import './exports.css';

const FORMAT_LABELS: Record<ExportFormat, string> = {
  project_bundle: 'Portable project (.flowwiz)',
  google_earth_kml: 'Google Earth KML',
  google_earth_kmz: 'Google Earth KMZ',
  calculation_report_pdf: 'Calculation report PDF',
  tabular_csv: 'CSV tables with units',
  machine_json: 'Machine-readable JSON',
  epanet_input: 'EPANET INP + mapping manifest',
};

const MAXIMUM_FLOWWIZ_BYTES = 64 * 1024 * 1024;

const SAVED_RESULT_FORMATS = new Set<ExportFormat>([
  'google_earth_kml',
  'google_earth_kmz',
  'calculation_report_pdf',
  'tabular_csv',
]);

const MAP_FORMATS = new Set<ExportFormat>(['google_earth_kml', 'google_earth_kmz']);
const OPTIMIZATION_DEFAULT_FORMATS: readonly ExportFormat[] = [
  'calculation_report_pdf',
  'google_earth_kml',
  'google_earth_kmz',
  'tabular_csv',
];

export interface ExportWorkspaceProps {
  api?: ExportApi;
  pollIntervalMs?: number;
  projectId: string | null;
}

function toggle<T>(values: T[], value: T): T[] {
  return values.includes(value)
    ? values.filter((item) => item !== value)
    : [...values, value];
}

function initialExportSelection(preview: ExportPreview): {
  formats: ExportFormat[];
  optimizationIds: string[];
  simulationIds: string[];
} {
  const simulationId = preview.available_simulation_results[0];
  if (
    simulationId !== undefined &&
    preview.supported_formats.includes('calculation_report_pdf')
  ) {
    return {
      formats: ['calculation_report_pdf'],
      optimizationIds: [],
      simulationIds: [simulationId],
    };
  }

  const optimizationId = preview.available_optimization_runs[0];
  const optimizationFormat = OPTIMIZATION_DEFAULT_FORMATS.find((format) =>
    preview.supported_formats.includes(format),
  );
  if (optimizationId !== undefined && optimizationFormat !== undefined) {
    return {
      formats: [optimizationFormat],
      optimizationIds: [optimizationId],
      simulationIds: [],
    };
  }

  return {
    formats: preview.supported_formats.includes('project_bundle')
      ? ['project_bundle']
      : [],
    optimizationIds: [],
    simulationIds: [],
  };
}

function savedResultSummary(
  simulationIds: string[],
  optimizationIds: string[],
): string {
  const parts: string[] = [];
  if (simulationIds.length > 0) {
    parts.push(
      `${simulationIds.length} ${
        simulationIds.length === 1 ? 'simulation' : 'simulations'
      }`,
    );
  }
  if (optimizationIds.length > 0) {
    parts.push(
      `${optimizationIds.length} ${
        optimizationIds.length === 1 ? 'optimization' : 'optimizations'
      }`,
    );
  }
  return parts.length === 0 ? 'None selected' : `${parts.join(' · ')} selected`;
}

function safeDownloadPath(
  path: string,
  runId: string,
  artifactId: string,
): string | null {
  const expected = `/api/v1/exports/runs/${encodeURIComponent(
    runId,
  )}/artifacts/${encodeURIComponent(artifactId)}`;
  return path === expected ? expected : null;
}

function readableState(value: string): string {
  return value.replaceAll('_', ' ');
}

function runTitle(run: ExportRunRecord): string {
  if (run.state === 'completed') return 'Export ready';
  if (run.state === 'failed') return 'Export failed';
  if (run.state === 'queued' || run.state === 'running') return 'Creating export';
  return `Export ${readableState(run.state)}`;
}

export function ExportWorkspace({
  api = exportApi,
  pollIntervalMs = 1000,
  projectId,
}: ExportWorkspaceProps) {
  const [preview, setPreview] = useState<Awaited<ReturnType<ExportApi['preview']>> | null>(
    null,
  );
  const [previewAttempt, setPreviewAttempt] = useState(0);
  const [previewLoading, setPreviewLoading] = useState(projectId !== null);
  const [formats, setFormats] = useState<ExportFormat[]>(['project_bundle']);
  const [simulationIds, setSimulationIds] = useState<string[]>([]);
  const [optimizationIds, setOptimizationIds] = useState<string[]>([]);
  const [attachmentIds, setAttachmentIds] = useState<string[]>([]);
  const [routeColorMode, setRouteColorMode] =
    useState<RouteColorMode>('pressure_utilization');
  const [includePermitted, setIncludePermitted] = useState(false);
  const [includeExclusions, setIncludeExclusions] = useState(false);
  const [run, setRun] = useState<ExportRunRecord | null>(null);
  const [exportBusy, setExportBusy] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bundleFile, setBundleFile] = useState<File | null>(null);
  const [bundlePreview, setBundlePreview] = useState<BundleImportPreview | null>(null);
  const [resolution, setResolution] = useState<ConflictResolution>('reject');
  const [importStatus, setImportStatus] = useState<ImportedProjectSnapshot | null>(null);
  const currentProjectIdRef = useRef(projectId);
  const importPreviewRequestRef = useRef(0);
  currentProjectIdRef.current = projectId;

  const latestProgress = useMemo(() => run?.progress.at(-1) ?? null, [run]);
  const active = run?.state === 'queued' || run?.state === 'running';
  const busy = exportBusy || importBusy;
  const requiresSimulation = formats.includes('epanet_input');
  const includesMap = formats.some((format) => MAP_FORMATS.has(format));
  const includesBundle = formats.includes('project_bundle');
  const hasOptions = includesMap || includesBundle;
  const selectedResultsSummary = savedResultSummary(
    simulationIds,
    optimizationIds,
  );

  useEffect(() => {
    setFormats(['project_bundle']);
    setSimulationIds([]);
    setOptimizationIds([]);
    setAttachmentIds([]);
    setRouteColorMode('pressure_utilization');
    setIncludePermitted(false);
    setIncludeExclusions(false);
    setRun(null);
    setExportBusy(false);
    setError(null);
  }, [projectId]);

  useEffect(() => {
    let ignore = false;
    setPreview(null);
    if (projectId === null) {
      setPreviewLoading(false);
      return () => {
        ignore = true;
      };
    }

    setPreviewLoading(true);
    setError(null);
    void api
      .preview(projectId)
      .then((nextPreview) => {
        if (!ignore) {
          const selection = initialExportSelection(nextPreview);
          setFormats(selection.formats);
          setSimulationIds(selection.simulationIds);
          setOptimizationIds(selection.optimizationIds);
          setPreview(nextPreview);
        }
      })
      .catch((cause: unknown) => {
        if (!ignore) {
          setError(
            cause instanceof Error ? cause.message : 'Export preview could not be loaded.',
          );
        }
      })
      .finally(() => {
        if (!ignore) setPreviewLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [api, previewAttempt, projectId]);

  useEffect(() => {
    if (!active || run === null) return undefined;
    let ignore = false;
    const runId = run.id;
    const requestedProjectId = projectId;
    const timer = window.setInterval(() => {
      void api
        .get(runId)
        .then((nextRun) => {
          if (!ignore && currentProjectIdRef.current === requestedProjectId) {
            setRun(nextRun);
          }
        })
        .catch((cause: unknown) => {
          if (!ignore && currentProjectIdRef.current === requestedProjectId) {
            setError(
              cause instanceof Error
                ? cause.message
                : 'Export progress could not be refreshed.',
            );
          }
        });
    }, pollIntervalMs);
    return () => {
      ignore = true;
      window.clearInterval(timer);
    };
  }, [active, api, pollIntervalMs, projectId, run]);

  function toggleFormat(format: ExportFormat) {
    const next = toggle(formats, format);
    setFormats(next);
    if (format === 'project_bundle' && !next.includes('project_bundle')) {
      setAttachmentIds([]);
    }
  }

  async function startExport() {
    const requestedProjectId = projectId;
    setError(null);
    setExportBusy(true);
    try {
      if (requestedProjectId === null) {
        throw new Error('Select a project before creating exports.');
      }
      if (preview === null || preview.project_id !== requestedProjectId) {
        throw new Error('Reload export data before creating an export.');
      }
      if (formats.length === 0) throw new Error('Select at least one export format.');
      if (formats.some((format) => !preview.supported_formats.includes(format))) {
        throw new Error('Reload export data before creating an export.');
      }
      if (
        simulationIds.some(
          (resultId) => !preview.available_simulation_results.includes(resultId),
        ) ||
        optimizationIds.some(
          (runId) => !preview.available_optimization_runs.includes(runId),
        ) ||
        attachmentIds.some(
          (attachmentId) =>
            !preview.available_source_attachments.some(
              (item) => item.attachment_id === attachmentId,
            ),
        )
      ) {
        throw new Error('Reload export data before creating an export.');
      }
      if (formats.includes('epanet_input') && simulationIds.length === 0) {
        throw new Error('Select a saved simulation result for EPANET export.');
      }
      if (
        formats.some((format) => SAVED_RESULT_FORMATS.has(format)) &&
        simulationIds.length === 0 &&
        optimizationIds.length === 0
      ) {
        throw new Error('Select a saved simulation result or optimization run for this export.');
      }
      const selection: ExportSelection = {
        formats,
        include_exclusion_overlays: includeExclusions,
        include_permitted_overlays: includePermitted,
        optimization_run_ids: optimizationIds,
        project_id: requestedProjectId,
        route_color_mode: routeColorMode,
        simulation_result_ids: simulationIds,
        source_attachment_ids: formats.includes('project_bundle') ? attachmentIds : [],
      };
      const createdRun = await api.create(selection);
      if (currentProjectIdRef.current === requestedProjectId) setRun(createdRun);
    } catch (cause) {
      if (currentProjectIdRef.current === requestedProjectId) {
        setError(cause instanceof Error ? cause.message : 'Export could not be started.');
      }
    } finally {
      if (currentProjectIdRef.current === requestedProjectId) setExportBusy(false);
    }
  }

  async function previewImport() {
    setBundlePreview(null);
    setImportStatus(null);
    if (bundleFile === null) return;
    if (!bundleFile.name.toLowerCase().endsWith('.flowwiz')) {
      setError('Select a file with the .flowwiz extension.');
      return;
    }
    if (bundleFile.size <= 0 || bundleFile.size > MAXIMUM_FLOWWIZ_BYTES) {
      setError('The .flowwiz file must be non-empty and no larger than 64 MiB.');
      return;
    }
    const requestId = importPreviewRequestRef.current + 1;
    importPreviewRequestRef.current = requestId;
    setImportBusy(true);
    setError(null);
    reportLifecycleDiagnostic('import_preview_started', 'exports', 'started');
    try {
      const nextPreview = await api.previewBundle(bundleFile);
      if (importPreviewRequestRef.current === requestId) {
        setBundlePreview(nextPreview);
        setResolution('reject');
        reportLifecycleDiagnostic('import_preview_completed', 'exports', 'succeeded');
      }
    } catch (cause) {
      if (importPreviewRequestRef.current === requestId) {
        setError(cause instanceof Error ? cause.message : 'Bundle preview failed.');
        reportLifecycleDiagnostic('import_preview_failed', 'exports', 'failed');
      }
    } finally {
      if (importPreviewRequestRef.current === requestId) setImportBusy(false);
    }
  }

  async function commitImport() {
    if (bundlePreview === null) return;
    setImportBusy(true);
    setError(null);
    try {
      const imported = await api.commitBundle(bundlePreview.preview_token, resolution);
      setImportStatus(imported);
      setBundlePreview(null);
      if (currentProjectIdRef.current === imported.project.id) {
        setFormats(['project_bundle']);
        setSimulationIds([]);
        setOptimizationIds([]);
        setAttachmentIds([]);
        setRouteColorMode('pressure_utilization');
        setIncludePermitted(false);
        setIncludeExclusions(false);
        setRun(null);
        setPreview(null);
        setPreviewAttempt((value) => value + 1);
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Bundle import failed.');
    } finally {
      setImportBusy(false);
    }
  }

  return (
    <Page title="Exports">
      <div className="exports-workspace">
        {error === null ? null : <p role="alert">{error}</p>}

        {projectId === null ? (
          <Card title="Select a project">
            <p>A selected project is required to create an export.</p>
          </Card>
        ) : previewLoading ? (
          <p role="status">Loading export data…</p>
        ) : preview === null ? (
          <Card title="Export data unavailable">
            <button type="button" onClick={() => setPreviewAttempt((value) => value + 1)}>
              Retry
            </button>
          </Card>
        ) : (
          <div
            className={[
              'exports-layout',
              run === null ? 'exports-layout--single' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <Card title={preview.project_name}>
              <details
                key={`saved-results-${preview.project_id}`}
                className="exports-selection-details"
              >
                <summary>
                  Saved results
                  <span>{selectedResultsSummary}</span>
                </summary>
                <fieldset>
                  <legend>
                    Simulations{requiresSimulation ? ' — required for EPANET' : ''}
                  </legend>
                  {preview.available_simulation_results.length === 0 ? (
                    <p>No saved simulation results.</p>
                  ) : (
                    <div className="exports-checks">
                      {preview.available_simulation_results.map((id) => (
                        <label key={id}>
                          <input
                            {...guided(deliveryFieldHelp.simulationResult, id)}
                            type="checkbox"
                            checked={simulationIds.includes(id)}
                            onChange={() => setSimulationIds(toggle(simulationIds, id))}
                          />
                          Result {id.slice(0, 8)}
                        </label>
                      ))}
                    </div>
                  )}
                </fieldset>
                <fieldset>
                  <legend>Optimizations and alternatives</legend>
                  {preview.available_optimization_runs.length === 0 ? (
                    <p>No saved optimization records.</p>
                  ) : (
                    <div className="exports-checks">
                      {preview.available_optimization_runs.map((id) => (
                        <label key={id}>
                          <input
                            {...guided(deliveryFieldHelp.optimizationRun, id)}
                            type="checkbox"
                            checked={optimizationIds.includes(id)}
                            onChange={() => setOptimizationIds(toggle(optimizationIds, id))}
                          />
                          Run {id.slice(0, 8)}
                        </label>
                      ))}
                    </div>
                  )}
                </fieldset>
              </details>

              <fieldset className="exports-formats">
                <legend>Formats</legend>
                <div className="exports-format-grid">
                  {preview.supported_formats.map((format) => (
                    <label key={format}>
                      <input
                        {...guided(deliveryFieldHelp.exportFormat, format)}
                        type="checkbox"
                        checked={formats.includes(format)}
                        onChange={() => toggleFormat(format)}
                      />
                      {FORMAT_LABELS[format]}
                    </label>
                  ))}
                </div>
              </fieldset>

              {hasOptions ? (
                <details className="exports-selection-details">
                  <summary>
                    Options
                    <span>
                      {attachmentIds.length + Number(includePermitted) + Number(includeExclusions)}{' '}
                      selected
                    </span>
                  </summary>
                  {includesMap ? (
                    <fieldset>
                      <legend>Map</legend>
                      <div className="exports-options">
                        <label>
                          Route color
                          <select
                            {...guided(deliveryFieldHelp.routeColor)}
                            value={routeColorMode}
                            onChange={(event) =>
                              setRouteColorMode(event.target.value as RouteColorMode)
                            }
                          >
                            <option value="pressure_utilization">Pressure utilization</option>
                            <option value="flow">Flow</option>
                            <option value="direction">Direction</option>
                            <option value="status">Constraint status</option>
                          </select>
                        </label>
                        <label>
                          <input
                            {...guided(deliveryFieldHelp.includePermitted)}
                            type="checkbox"
                            checked={includePermitted}
                            onChange={(event) => setIncludePermitted(event.target.checked)}
                          />
                          Include permitted-route overlays
                        </label>
                        <label>
                          <input
                            {...guided(deliveryFieldHelp.includeExclusions)}
                            type="checkbox"
                            checked={includeExclusions}
                            onChange={(event) => setIncludeExclusions(event.target.checked)}
                          />
                          Include exclusion overlays
                        </label>
                      </div>
                    </fieldset>
                  ) : null}
                  {includesBundle ? (
                    <fieldset>
                      <legend>Source attachments</legend>
                      <p id="export-attachment-note">
                        Excluded by default. Credentials and local paths are rejected.
                      </p>
                      {preview.available_source_attachments.length === 0 ? (
                        <p>No source attachments.</p>
                      ) : (
                        <div className="exports-checks">
                          {preview.available_source_attachments.map((item) => (
                            <label key={item.attachment_id}>
                              <input
                                {...guided(
                                  deliveryFieldHelp.sourceAttachment,
                                  item.attachment_id,
                                )}
                                aria-describedby="export-attachment-note"
                                type="checkbox"
                                checked={attachmentIds.includes(item.attachment_id)}
                                onChange={() =>
                                  setAttachmentIds(
                                    toggle(attachmentIds, item.attachment_id),
                                  )
                                }
                              />
                              <span>
                                {item.filename} · {item.byte_size.toLocaleString()} bytes
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                    </fieldset>
                  ) : null}
                </details>
              ) : null}

              {preview.warnings.map((warning) => (
                <p className="exports-warning" key={warning}>
                  Saved warning: {warning}
                </p>
              ))}
              <details className="exports-verification">
                <summary>Source snapshot</summary>
                <dl>
                  <div>
                    <dt>Equivalence hash</dt>
                    <dd>
                      <code>{preview.snapshot_equivalence_sha256}</code>
                    </dd>
                  </div>
                </dl>
              </details>
              <button
                className="button button--primary exports-create"
                disabled={busy || active}
                type="button"
                onClick={() => void startExport()}
              >
                {exportBusy
                  ? 'Creating…'
                  : run?.state === 'failed'
                    ? 'Retry export'
                    : 'Create export'}
              </button>
            </Card>

            {run === null ? null : (
              <Card title={runTitle(run)}>
                <section className="exports-run" aria-live="polite">
                  <p className="exports-run__state">
                    <strong>{readableState(run.state)}</strong>
                    {latestProgress?.message === undefined
                      ? null
                      : ` · ${latestProgress.message}`}
                  </p>
                  <label htmlFor="export-progress">Progress</label>
                  <progress
                    id="export-progress"
                    max={1}
                    value={latestProgress?.completed_fraction ?? 0}
                  />
                  {run.failures.map((failure) => (
                    <p role="alert" key={failure}>
                      {failure}
                    </p>
                  ))}
                  {run.artifacts.length === 0 ? null : (
                    <ul className="exports-artifacts">
                      {run.artifacts.map((artifact) => {
                        const downloadPath = safeDownloadPath(
                          artifact.download_path,
                          run.id,
                          artifact.id,
                        );
                        return (
                          <li key={artifact.id}>
                            <div>
                              <strong>{artifact.filename}</strong>
                              <span>{artifact.byte_size.toLocaleString()} bytes</span>
                            </div>
                            {downloadPath === null ? (
                              <span>Download unavailable</span>
                            ) : (
                              <a
                                aria-label={`Download ${artifact.filename}`}
                                className="button button--secondary"
                                href={downloadPath}
                                download={artifact.filename}
                              >
                                Download
                              </a>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  <details className="exports-verification">
                    <summary>Export verification</summary>
                    <dl>
                      <div>
                        <dt>Run</dt>
                        <dd>{run.id}</dd>
                      </div>
                      {run.artifacts.map((artifact) => (
                        <div key={artifact.id}>
                          <dt>{artifact.filename} SHA-256</dt>
                          <dd>
                            <code>{artifact.sha256}</code>
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </details>
                </section>
              </Card>
            )}
          </div>
        )}

        <Card title="Import">
          <details
            key={projectId === null ? 'no-project' : projectId}
            className="exports-import-details"
            open={projectId === null ? true : undefined}
          >
            <summary>Portable project (.flowwiz)</summary>
            <div className="exports-import">
              <label className="exports-file">
                File
                <input
                  {...guided(deliveryFieldHelp.bundleFile)}
                  accept=".flowwiz,application/zip"
                  disabled={busy || active}
                  onChange={(event) => {
                    const selected = event.target.files?.[0] ?? null;
                    importPreviewRequestRef.current += 1;
                    setImportBusy(false);
                    setError(null);
                    reportLifecycleDiagnostic(
                      selected === null
                        ? 'file_selection_cancelled'
                        : 'file_selection_completed',
                      'exports',
                      selected === null ? 'cancelled' : 'succeeded',
                    );
                    setBundleFile(selected);
                    setBundlePreview(null);
                    setImportStatus(null);
                  }}
                  onClick={() =>
                    reportLifecycleDiagnostic('file_selection_opened', 'exports', 'started')
                  }
                  type="file"
                />
              </label>
              <button
                disabled={bundleFile === null || busy || active}
                type="button"
                onClick={() => void previewImport()}
              >
                {importBusy ? 'Checking…' : 'Preview import'}
              </button>

              {bundlePreview === null ? null : (
                <section className="exports-import-preview" aria-live="polite">
                  <h3>{bundlePreview.project_name}</h3>
                  <dl className="exports-import-counts">
                    <div>
                      <dt>Simulations</dt>
                      <dd>{bundlePreview.simulation_count}</dd>
                    </div>
                    <div>
                      <dt>Optimizations</dt>
                      <dd>{bundlePreview.optimization_count}</dd>
                    </div>
                    <div>
                      <dt>Attachments</dt>
                      <dd>{bundlePreview.attachment_count}</dd>
                    </div>
                  </dl>
                  {bundlePreview.migrations_applied.map((item) => (
                    <p key={item}>Migration: {item}</p>
                  ))}
                  {bundlePreview.warnings.map((warning) => (
                    <p className="exports-warning" key={warning}>
                      Bundle warning: {warning}
                    </p>
                  ))}
                  {bundlePreview.conflicts.map((conflict) => (
                    <p
                      className="exports-warning"
                      key={`${conflict.kind}-${conflict.entity_id}`}
                    >
                      {conflict.message}
                    </p>
                  ))}
                  {bundlePreview.conflicts.length === 0 ? null : (
                    <label className="exports-resolution">
                      Conflict resolution
                      <select
                        {...guided(deliveryFieldHelp.conflictResolution)}
                        value={resolution}
                        onChange={(event) =>
                          setResolution(event.target.value as ConflictResolution)
                        }
                      >
                        <option value="reject">Keep existing project</option>
                        <option value="replace">Replace existing project atomically</option>
                      </select>
                    </label>
                  )}
                  {bundlePreview.conflicts.length > 0 && resolution === 'replace' ? (
                    <p className="exports-warning">
                      Existing project data will be replaced atomically.
                    </p>
                  ) : null}
                  <button
                    className="button button--primary"
                    disabled={
                      busy ||
                      active ||
                      (bundlePreview.conflicts.length > 0 && resolution === 'reject')
                    }
                    type="button"
                    onClick={() => void commitImport()}
                  >
                    {importBusy
                      ? 'Importing…'
                      : bundlePreview.conflicts.length > 0
                        ? 'Replace project'
                        : 'Import project'}
                  </button>
                  <details className="exports-verification">
                    <summary>Import preview verification</summary>
                    <dl>
                      <div>
                        <dt>Schema</dt>
                        <dd>{bundlePreview.bundle_schema_version}</dd>
                      </div>
                      <div>
                        <dt>Equivalence hash</dt>
                        <dd>
                          <code>{bundlePreview.equivalence_sha256}</code>
                        </dd>
                      </div>
                      <div>
                        <dt>Expanded bytes</dt>
                        <dd>{bundlePreview.total_uncompressed_bytes.toLocaleString()}</dd>
                      </div>
                      {bundlePreview.conflicts.map((conflict) => (
                        <div key={`hash-${conflict.kind}-${conflict.entity_id}`}>
                          <dt>{conflict.entity_id}</dt>
                          <dd>
                            Existing <code>{conflict.existing_sha256 ?? 'none'}</code>
                            <br />
                            Incoming <code>{conflict.incoming_sha256}</code>
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </details>
                </section>
              )}

              {importStatus === null ? null : (
                <div className="exports-import-status" role="status">
                  <strong>Imported {importStatus.project.name}.</strong>
                  <details className="exports-verification">
                    <summary>Imported project verification</summary>
                    <code>{importStatus.equivalence_sha256}</code>
                  </details>
                </div>
              )}
            </div>
          </details>
        </Card>
      </div>
    </Page>
  );
}
